/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Voordelen and Aandachtspunten DERIVED from the coded terms
 * (src/lib/termsVerdict.ts, D2): the same fact reads the same on every
 * provider. This component only words the items (`offer_terms.bullet.*`, with
 * their variables formatted per locale) and draws them.
 *
 * The DOT carries the kind, so a reader sees at a glance what sort of
 * aandachtspunt it is: a filled emerald dot is a pluspunt, a filled amber dot a
 * catch, a HOLLOW muted ring the "niet gecontroleerd / niet vermeld" family
 * (every bullet of NOT_CHECKED_FAMILY_KEYS — absence reads as absence, never as
 * a warning), a filled slate dot the offer's verdienmodel, which leads the
 * aandachtspunten and explains rather than warns. The editorial bullet — at
 * most one per side, sourced — is always LAST in its list and, where the
 * surface may link out, is followed by
 * " · bron": `<a rel="noopener noreferrer nofollow">` whose title carries the
 * verbatim quote. Where it may not (the compare pages never link out,
 * `linkSources` false), the quote stays for screen readers (sr-only) and no
 * anchor exists at all. A source that is not an http(s) address is never a link
 * either.
 *
 * A staffel's verdienmodel point is the one item drawn as a structure: its lead
 * ("Verdienmodel: staffel") over the ladder (src/components/offers/StaffelLadder.tsx,
 * a row per band, the band at the surface's volume emphasised) — the same in
 * the panel, on the profile page and in a compare page's cell. Every other
 * point is one line; a text-only surface prints the staffel inline
 * (termsText).
 *
 * Use: `const { pros, cons } = termsVerdict({ …, language });` then
 * `<DerivedBullets pros={pros} cons={cons} variant="panel" linkSources={…}
 * headingLevel={4} />`.
 *
 * Variants (plan §6.2, §6.5, §6.3; the type is the chosen Phase 0 composition):
 *   panel  the expanded row's panel — headings + two lists, 14 px (13 px on the
 *          phone) on 20 px lines, the heading tiles as on the live panel
 *   page   the profile page's "Voordelen en aandachtspunten" — the live
 *          profile type (14 px, 16 px from md)
 *   cell   one band cell of the compare table — NO headings (the row's label
 *          names the band), 13 px (11 px on a phone block)
 *
 * Renders both lists as siblings (a fragment: the parent's grid places them);
 * `side` restricts it to one list, which is how the compare table prints a band
 * cell and how a panel may place the two lists in two grid cells. An empty list
 * prints "Geen" (muted, italic) so a column never stands blank.
 *
 * Pure markup — no state, no effect — so the baked HTML and the hydrating
 * client agree.
 */

import React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { fillTemplate } from '../../lib/templateVars.js';
import type { FactTone } from '../../lib/termsTone';
import { staffelLadderRows } from '../../lib/offerPricingText';
import { NOT_CHECKED_FAMILY_KEYS, type VerdictItem } from '../../lib/termsVerdict';
import type { PublicEditorialBullet } from '../../types';
import { StaffelLadder } from '../offers/StaffelLadder';
import { termsLadderParts, useTermsText } from './termsText';
import { TONE_DOT, TONE_TEXT } from './toneText';

type EditorialItem = Extract<VerdictItem, { topic: 'editorial' }>;
type Side = 'pros' | 'cons';
type Variant = 'panel' | 'page' | 'cell';

export interface DerivedBulletsProps {
  /**
   * The derived pluspunten, in the verdict's fixed order (its editorial one last);
   * in a comparison led by the relative ones (src/lib/relativeBullets.ts, D36).
   */
  pros: readonly VerdictItem[];
  /** The derived aandachtspunten, likewise. */
  cons: readonly VerdictItem[];
  /**
   * The provider's public editorial bullets, for a caller that derived the lists
   * WITHOUT them: appended last to their list. A list that already holds an
   * editorial item (what `termsVerdict` returns) is left alone.
   */
  editorial?: { pro?: PublicEditorialBullet | null; con?: PublicEditorialBullet | null };
  variant: Variant;
  /** May the editorial bullet link to its source? False on the compare pages. */
  linkSources: boolean;
  /** The level of the two headings (not printed by `cell`). */
  headingLevel: 2 | 3 | 4 | 5 | 6;
  /** Print only one list. */
  side?: Side;
  /** Stable prefix for the item keys when several lists render on one page. */
  keyPrefix?: string;
  /** Classes for each list's block (its grid placement is the parent's business). */
  className?: string;
}

// ── Look, per variant ────────────────────────────────────────────────────────

const HEADING: Record<Exclude<Variant, 'cell'>, string> = {
  // 14 px on a 20 px line (11 px on the phone) beside the 20 px tile
  panel:
    'mb-2 flex items-center gap-2 text-[11px] font-black uppercase leading-5 tracking-[0.2em] text-fg-muted md:mb-2.5 md:text-sm',
  // the live profile page's heading
  page: 'mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-fg-muted md:text-sm',
};

const LIST: Record<Variant, string> = {
  panel: 'flex flex-col gap-1.5',
  page: 'flex flex-col gap-2',
  cell: 'flex flex-col gap-[5px] sm:gap-1.5',
};

const ITEM: Record<Variant, string> = {
  panel:
    'flex items-start gap-2 text-[13px] font-medium leading-[18px] text-slate-300 md:text-sm md:leading-5',
  page: 'flex items-start gap-2 text-sm font-medium text-slate-300 md:text-base',
  cell: 'flex items-start gap-[5px] text-[11px] font-medium leading-[15px] text-slate-300 sm:gap-2 sm:text-[13px] sm:leading-[18px]',
};

/** The dot sits on the first line's centre: (line height − dot) / 2. */
const DOT: Record<Variant, string> = {
  panel: 'mt-[6px] h-1.5 w-1.5 md:mt-[7px]',
  page: 'mt-2 h-1.5 w-1.5',
  cell: 'mt-[5px] h-[5px] w-[5px] sm:mt-1.5 sm:h-1.5 sm:w-1.5',
};

/**
 * A staffel's ladder under its point (StaffelLadder), the same in every
 * variant: 11 px on a phone, 12 px from sm, on 16 px rows. The bands read
 * muted; the band at the surface's volume in the neutral tone's colour and
 * the point's own weight — the visitor sees their band without a sentence.
 */
const LADDER = 'mt-1 text-[11px] leading-4 sm:text-xs';
const LADDER_ROW = 'font-normal text-fg-muted';
const LADDER_CURRENT = cn('font-medium', TONE_TEXT.neutral);

// ── Rules ────────────────────────────────────────────────────────────────────

const NOT_CHECKED_FAMILY = new Set(NOT_CHECKED_FAMILY_KEYS);

/**
 * The tone a bullet is drawn in: every pluspunt is emerald; an aandachtspunt is
 * amber unless it belongs to the "niet gecontroleerd / niet vermeld" family
 * (hollow, muted) or is the offer's verdienmodel — a fact to understand, not a
 * catch, so a filled slate dot (neutral; slate-400 is 5.7:1 on the dark row,
 * the light page remaps it to #334155). The redactie's own aandachtspunt is a
 * catch, so amber.
 */
export function bulletTone(side: Side, item: VerdictItem): FactTone {
  if (side === 'pros') return 'good';
  if (item.topic === 'editorial') return 'watch';
  if (item.topic === 'pricingModel') return 'neutral';
  return NOT_CHECKED_FAMILY.has(item.key) ? 'unknown' : 'watch';
}

/** http(s) only: a source that is anything else (a `javascript:` URL) is no link. */
function safeHttpUrl(value: string | null | undefined): string | null {
  const url = (value ?? '').trim();
  return /^https?:\/\/\S+$/i.test(url) ? url : null;
}

/** The editorial bullet as an item, in the active language (the English line falls back to Dutch). */
function editorialItem(
  bullet: PublicEditorialBullet | null | undefined,
  language: 'nl' | 'en'
): EditorialItem | null {
  if (!bullet) return null;
  const text = (language === 'en' ? bullet.en || bullet.nl : bullet.nl) || '';
  if (!text.trim()) return null;
  return { topic: 'editorial', text, sourceUrl: bullet.sourceUrl ?? '', quote: bullet.quote ?? '' };
}

// ── Component ────────────────────────────────────────────────────────────────

export const DerivedBullets: React.FC<DerivedBulletsProps> = ({
  pros,
  cons,
  editorial,
  variant,
  linkSources,
  headingLevel,
  side,
  keyPrefix = 'bullets',
  className,
}) => {
  const { t, language, keyText } = useTermsText();

  const withEditorial = (
    items: readonly VerdictItem[],
    extra: PublicEditorialBullet | null | undefined
  ): readonly VerdictItem[] => {
    // The verdict puts its editorial bullet last in the list itself.
    if (items.some((item) => item.topic === 'editorial')) return items;
    const item = editorialItem(extra, language);
    return item ? [...items, item] : items;
  };

  /** The words of one item — and, for the redactie's, its source. */
  const renderItem = (item: VerdictItem): React.ReactNode => {
    if (item.topic !== 'editorial') return keyText(item.key, item.vars);

    const href = linkSources ? safeHttpUrl(item.sourceUrl) : null;
    const quote = item.quote.trim();
    const quoted = quote ? fillTemplate(t('offer_terms.source_quote'), { quote }) : '';
    // One text node per piece (React's server renderer puts a comment between
    // two neighbours, and every byte here is baked into the page ten times).
    return (
      <>
        {/* The space stays outside the link's span: a line may break before "· bron", never inside it. */}
        {href ? `${item.text} ` : item.text}
        {href ? (
          <span className="whitespace-nowrap font-semibold text-fg-muted">
            {'· '}
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              title={quoted || undefined}
              className="rounded-sm underline underline-offset-2 transition-colors hover:text-emerald-400 focus-ring"
            >
              {t('offer_terms.source')}
            </a>
          </span>
        ) : quoted ? (
          <span className="sr-only">{` ${quoted}`}</span>
        ) : null}
      </>
    );
  };

  /**
   * A staffel's verdienmodel point: its lead ("Verdienmodel: staffel") over the
   * ladder, one row per band, the band at the surface's volume emphasised —
   * the inline "25% tot 3.500 kWh · …" is for text-only surfaces. Null for
   * every other item, which stays one line.
   */
  const renderLadder = (item: VerdictItem): React.ReactNode => {
    if (item.topic === 'editorial') return null;
    const parts = termsLadderParts(t, language, item.key, item.vars);
    if (!parts) return null;
    return (
      <div className="min-w-0 break-words">
        {parts.lead}
        <StaffelLadder
          rows={staffelLadderRows(t, language, parts.ladder.tiers, parts.ladder.atKwh)}
          className={LADDER}
          rowClassName={LADDER_ROW}
          currentClassName={LADDER_CURRENT}
        />
        {parts.rest && <span className="block">{parts.rest}</span>}
      </div>
    );
  };

  const list = (which: Side, items: readonly VerdictItem[]) => (
    <ul className={LIST[variant]}>
      {items.length === 0 ? (
        <li className={cn(ITEM[variant], 'italic text-fg-muted')}>{t('table.none')}</li>
      ) : (
        items.map((item, i) => (
          <li key={`${keyPrefix}-${which}-${i}`} className={ITEM[variant]}>
            <span
              aria-hidden="true"
              className={cn(
                'shrink-0 rounded-full',
                DOT[variant],
                TONE_DOT[bulletTone(which, item)]
              )}
            />
            {renderLadder(item) ?? <span className="min-w-0 break-words">{renderItem(item)}</span>}
          </li>
        ))
      )}
    </ul>
  );

  const block = (which: Side, items: readonly VerdictItem[]) => {
    if (variant === 'cell') return <div className={className}>{list(which, items)}</div>;
    const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    const good = which === 'pros';
    return (
      <div className={cn('min-w-0', className)}>
        <Heading className={HEADING[variant]}>
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-md',
              good ? 'bg-emerald-900/30' : 'bg-red-900/30'
            )}
          >
            {good ? (
              <Check className="h-3 w-3 text-emerald-500" aria-hidden="true" />
            ) : (
              <X className="h-3 w-3 text-red-500" aria-hidden="true" />
            )}
          </span>
          {t(good ? 'table.pros' : 'table.cons')}
        </Heading>
        {list(which, items)}
      </div>
    );
  };

  const proItems = withEditorial(pros, editorial?.pro);
  const conItems = withEditorial(cons, editorial?.con);

  if (side) return block(side, side === 'pros' ? proItems : conItems);
  const both = (
    <>
      {block('pros', proItems)}
      {block('cons', conItems)}
    </>
  );
  // A band cell holding both lists stacks them with a gap; headed blocks are
  // siblings of the parent's grid.
  return variant === 'cell' ? <div className="space-y-2">{both}</div> : both;
};
